import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { CalendarDays, Clock, DollarSign, Users, Target, FileText } from "lucide-react";
import Navbar from "@/components/Navbar";

const networkParticipants = [
  {
    name: "Julia De Rosi",
    role: "Creator",
    title: "GSA Solution Architect",
    avatar: "/lovable-uploads/60bb35da-bbd1-4b37-8c2f-3683d80d76d2.png",
    color: "bg-blue-500"
  },
  {
    name: "Charles Hardy", 
    role: "Composer",
    title: "Federal Construction Expert",
    avatar: "/lovable-uploads/9f6af481-beaf-4089-9884-09d2670e55fc.png",
    color: "bg-green-500"
  },
  {
    name: "Heather Edwards",
    role: "Contributor", 
    title: "Project Management Specialist",
    avatar: "/lovable-uploads/c44899b0-f5fd-437e-b709-a608a7173b7a.png",
    color: "bg-purple-500"
  },
  {
    name: "Giblet",
    role: "Confirmer",
    title: "AI Knowledge Curator",
    avatar: "/lovable-uploads/39eec67f-00ba-49ce-97bf-f038a8f64e1b.png",
    color: "bg-orange-500"
  }
];

const GsaChallenge1BountyDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [challengeData, setChallengeData] = useState<any>(null);

  useEffect(() => {
    if (location.state) {
      setChallengeData(location.state);
    }
  }, [location]);

  if (!challengeData) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <p className="text-gray-600 mb-4">Challenge data not found.</p>
            <Button onClick={() => navigate("/gsa-channel")}>
              Back to GSA Channel
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-8">
          <img 
            src="/lovable-uploads/bb3f5ac9-6c4b-494d-9a60-86ee7a3feb77.png" 
            alt="iSPAI Logo" 
            className="h-16 mx-auto mb-6 cursor-pointer"
            onClick={() => navigate("/gsa-channel")}
          />
          <h1 className="text-3xl font-bold mb-4">GSA Challenge #1 - Published Successfully</h1>
          <p className="text-lg text-gray-600">
            Your federal office renovation timeline challenge is now live on the GSA knowledge network
          </p>
        </div>

        {/* Challenge Summary */}
        <Card className="mb-8 border-green-200 bg-green-50">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Target className="h-6 w-6 text-green-600" />
              <CardTitle className="text-xl text-green-800">Challenge Published</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-green-900 font-medium text-lg mb-4">
              {challengeData.challenge}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-green-600" />
                <div>
                  <p className="text-sm text-green-700">Bounty Amount</p>
                  <p className="font-semibold text-green-900">{challengeData.bountyAmount} tokens</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-green-600" />
                <div>
                  <p className="text-sm text-green-700">Timeline</p>
                  <p className="font-semibold text-green-900">{challengeData.timeline} days</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <CalendarDays className="h-5 w-5 text-green-600" />
                <div>
                  <p className="text-sm text-green-700">Published</p>
                  <p className="font-semibold text-green-900">{new Date().toLocaleDateString()}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-green-600" />
                <div>
                  <p className="text-sm text-green-700">Network Size</p>
                  <p className="font-semibold text-green-900">4 experts</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Network Participants */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-6 w-6" />
              GSA Expert Network
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {networkParticipants.map((participant, index) => (
                <div key={index} className="text-center">
                  <Avatar className="h-20 w-20 mx-auto mb-3">
                    <AvatarImage src={participant.avatar} alt={participant.name} className="object-cover scale-90" />
                    <AvatarFallback className={participant.color + " text-white"}>
                      {participant.name.split(' ').map(n => n[0]).join('')}
                    </AvatarFallback>
                  </Avatar>
                  <h3 className="font-semibold text-gray-900">{participant.name}</h3>
                  <p className="text-sm text-gray-600 mb-2">{participant.title}</p>
                  <Badge variant="secondary" className="text-xs">
                    {participant.role}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Selected Functional Areas */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Selected Functional Areas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {challengeData.selectedAreas?.map((area: string, index: number) => (
                <Badge key={index} variant="outline" className="px-3 py-1">
                  {area}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Requirements Preview */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-6 w-6" />
              Challenge Requirements
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-gray-50 p-4 rounded-lg">
              <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono">
                {challengeData.requirements?.substring(0, 500)}...
              </pre>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-center space-x-4">
          <Button 
            variant="outline" 
            onClick={() => navigate("/gsa-channel")}
            className="px-8 py-2"
          >
            Back to GSA Channel
          </Button>
          
          <Button 
            onClick={() => navigate("/gsa-challenge-1/creator-rewards")}
            className="bg-[#14213d] hover:bg-[#0d1829] text-white px-8 py-2"
          >
            View Creator Dashboard
          </Button>
        </div>
      </main>
    </div>
  );
};

export default GsaChallenge1BountyDetails;